/** Service Desk console: tracker broadcast, split, similar tickets, translate, knowledge drafts, capacity caps, read receipts and the weekly summary (SD-S4-04 / 05 / 08). */
export const serviceExtras = {
  tracker: {
    title: "Tracker",
    notTracker: "This is not a tracker ticket.",
    notTrackerHint: "A tracker follows one cause - an outage, a recall - across many client tickets. Give the ticket a tracker type to send one update to all of them.",
    linkedTitle: "Linked tickets",
    linkedNone: "No tickets are linked to this tracker yet. Link a client ticket to it as \"caused by\" and it will appear here.",
    customer: "Client",
    internalOnly: "Internal",
    hidden: "{{count}} linked ticket(s) you cannot open are not listed.",
    limit: "A tracker updates at most {{max}} tickets at once.",
    composerLabel: "Update for the linked tickets",
    composerPlaceholder: "What changed? Crews are on site, service is back...",
    publicLabel: "Public note",
    publicHint: "On: the client of each ticket reads it as a reply. Off: it is an internal note on each ticket.",
    send: "Send update",
    confirmTitle: "Send a public update to {{count}} ticket(s)?",
    confirmBody: "Each client will read this as a reply from your team. You can't take it back.",
    confirmSend: "Send to {{count}} ticket(s)",
    sentPublic: "Public update sent to {{count}} ticket(s)",
    sentInternal: "Internal note added to {{count}} ticket(s)",
    skipped: "{{count}} ticket(s) were skipped.",
    skip: {
      no_access: "You can't write on it",
      closed: "It is closed",
      not_customer: "Not a client ticket",
      failed: "It could not be updated",
    },
    error: "The tracker could not be loaded. Try again.",
    retry: "Try again",
    empty: "Write the update first.",
  },
  split: {
    title: "Split",
    intro: "Copy some messages into a new ticket for the same client. The original keeps its whole conversation.",
    open: "Split messages into a new ticket",
    dialogTitle: "Split into a new ticket",
    dialogBody: "Choose the messages to copy. The new ticket has the same requester and organisation and is linked to {{number}}.",
    titleLabel: "Title of the new ticket",
    titleHint: "Leave empty to use \"{{title}} (split)\".",
    choose: "Messages to copy",
    internalNote: "Internal note",
    selected: "{{count}} message(s) selected",
    pickOne: "Choose at least one message.",
    submit: "Create ticket",
    done: "Created {{number}} with {{count}} message(s)",
    noMessages: "There are no messages to split yet.",
    error: "The ticket could not be split.",
    view: "Open {{number}}",
  },
  seen: {
    label: "Seen {{when}}",
    aria: "The client opened this reply on {{when}}",
  },
  copilot: {
    title: "AI assist",
    off: "Staff AI is switched off for this workspace.",
    noCredits: "There are not enough credits for staff AI.",
    unavailable: "The AI model did not answer. Nothing was charged.",
    retry: "Try again",
    similar: {
      title: "Similar resolved tickets",
      intro: "What was done the last time. Your client's organisation first, then the rest of the workspace. Free.",
      find: "Find similar tickets",
      finding: "Looking",
      noneTitle: "Nothing similar yet",
      none: "No similar resolved tickets yet. Resolve tickets with a clear note and they will show up here.",
      again: "Search again",
      organisation: "Same organisation",
      workspace: "Other organisation",
      noNote: "No resolution note.",
      match: "{{percent}}% alike",
      error: "Similar tickets could not be loaded.",
      graph: {
        badge: "Ranked with related records",
        hint: "Equipment, product, links and organisation count as well as the words.",
      },
      why: {
        label: "Why it matches",
        more: "{{count}} more reasons",
        same_asset: "Same equipment",
        same_product: "Same product",
        linked: "Linked ticket",
        merged: "Merged",
        shared_problem: "Same linked problem",
        same_organisation: "Same organisation",
        same_site: "Same site",
        same_requester: "Same requester",
        same_category: "Same category",
        shared_tags: "Shared tags ({{count}})",
        same_resolution: "Same resolution",
        same_sla_breach: "Same SLA breach",
        has_article: "Has a knowledge article",
      },
    },
    translate: {
      action: "Translate",
      toFil: "To Filipino",
      toEn: "To English",
      working: "Translating",
      original: "Original",
      result: "Translation to {{language}}",
      languageFil: "Filipino",
      languageEn: "English",
      copy: "Use in reply",
      copied: "Added to your reply box",
      close: "Hide translation",
      note: "Shown only to you. It is never sent by itself. 1 credit.",
      error: "The translation did not work.",
    },
    draft: {
      title: "Knowledge article",
      intro: "Turn what solved this ticket into an article. It stays a draft until you approve it; personal details are masked.",
      create: "Draft an article from this ticket",
      creating: "Drafting",
      openExists: "This ticket already has a draft below. Approve or discard it to draft another.",
      costs: "1 credit.",
      notResolved: "Resolve the ticket with a resolution note to draft an article from it.",
      none: "No drafts yet.",
      status: { draft: "Draft", approved: "Approved", discarded: "Discarded" },
      edit: "Edit",
      save: "Save changes",
      approve: "Approve and add to knowledge",
      approveTitle: "Add this article to your knowledge?",
      approveBody: "It becomes a knowledge source that only your team can read until you change who may see it.",
      approved: "Added to your knowledge",
      openSource: "Open in Knowledge",
      discard: "Discard",
      discarded: "Draft discarded",
      titleLabel: "Title",
      bodyLabel: "Article",
      created: "Draft ready to review",
      error: "The draft could not be made.",
      needKnowledge: "You also need permission to create knowledge to approve it.",
      removed: "{{count}} statement(s) the ticket does not support were left out.",
    },
  },
  transcript: {
    download: "Download PDF",
    failed: "The PDF could not be downloaded. Try again.",
  },
  fieldAssist: {
    intro: "Turn your saved notes into a short summary the client can read. Wait for Saved first; the summary uses what is saved.",
    run: "Write a client-friendly summary",
    failed: "The summary could not be written. Nothing was charged; try again.",
    result: "Summary for the client (edit it before you use it)",
    use: "Use in the report",
    discard: "Discard",
    note: "1 credit. Check every sentence: it is never sent by itself.",
  },
  capacity: {
    title: "Capacity caps",
    intro: "The most open tickets a person is given automatically. Someone at their cap is skipped by routing rules; a manager can still assign to them by hand.",
    member: "Member",
    cap: "Cap",
    holding: "{{count}} open now",
    atCapacity: "At capacity",
    placeholder: "No cap",
    save: "Save",
    saved: "Capacity saved for {{name}}",
    invalid: "Use a whole number from {{min}} to {{max}}, or leave it empty for no cap.",
    empty: "There are no active members yet.",
    error: "Capacity could not be loaded.",
    retry: "Try again",
    atCapacityNote: "Everyone who could take it is at their capacity cap, so it waits in the queue.",
  },
  switches: {
    title: "Read receipts and summaries",
    receiptsTitle: "Show clients' \"Seen\" on staff replies",
    receiptsHint: "When on, you see the time a client first opened a reply in the portal or the widget. Off by default.",
    receiptsPrivacy: "Privacy: only the time of the first opening is kept. Nothing is recorded for internal notes or for clients who read by e-mail.",
    receiptsSaved: "Read receipts are now {{state}}",
    on: "on",
    off: "off",
    summaryTitle: "Weekly Service summary e-mail",
    summaryHint: "Every Monday, the numbers for last week - tickets opened and resolved, open now, rating - and the numbers of the oldest open tickets. No client details.",
    summaryNotEligible: "You need permission to see reports to get the summary.",
    summarySaved: "Weekly summary is now {{state}} for you",
    error: "These settings could not be saved.",
    loading: "Loading",
  },
}
